import type { InferOutput } from "valibot";
import { boolean, object } from "valibot";
import { initIntegrationDefinition } from "../Integration";
import type { IncompleteIntegrationDefinition } from "../types";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { MAX_CHARACTERS_SM, PORT_MAX_VALUE, PORT_MIN_VALUE } from "../../constants";
import { NumberInput } from "../../components/inputs/NumberInput";
import { Checkbox } from "../../components/checkbox/Checkbox";
import { integerValue, requireText } from "../../utils/validation/base";
import { integrationsIcons } from "../IntegrationIcons";
import { keyFromSelector } from "i18next";

type ConfigurationDto = {
  host: string;
  port: number;
  useSSL: boolean;
  username?: string;
  password?: string;
  database?: string;
  collection?: string;
};

const Schema = object({
  host: requireText(MAX_CHARACTERS_SM),
  port: integerValue(PORT_MIN_VALUE, PORT_MAX_VALUE),
  useSSL: boolean(),
  username: requireText(MAX_CHARACTERS_SM),
  password: requireText(MAX_CHARACTERS_SM),
  database: requireText(MAX_CHARACTERS_SM),
  collection: requireText(MAX_CHARACTERS_SM),
});

export type MongoDBIntegrationConfiguration = InferOutput<typeof Schema>;
const MongoDBIntegration: IncompleteIntegrationDefinition<MongoDBIntegrationConfiguration, ConfigurationDto> = {
  type: "mongodbinsert",
  legacy: {
    namespace: "com.moreapp",
    key: "mongodbinsert",
    version: 4,
  },
  schema: Schema,
  icon: integrationsIcons["mongodbinsert"],
  titleKey: keyFromSelector(($) => $.MONGODB_INTEGRATION_TITLE),
  descriptionKey: keyFromSelector(($) => $.MONGODB_INTEGRATION_DESCRIPTION),
  defaultProperties: () => ({
    host: "",
    port: 27017,
    useSSL: false,
    username: "",
    password: "",
    database: "",
    collection: "",
  }),
  verifiable: true,
  propertyKeys: {
    host: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_HOST_LABEL),
    port: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_PORT_LABEL),
    useSSL: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_SSL_LABEL),
    username: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_USERNAME_LABEL),
    password: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_PASSWORD_LABEL),
    database: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_DATABASE_LABEL),
    collection: keyFromSelector(($) => $.MONGODB_INTEGRATION_CONFIGURATION_COLLECTION_LABEL),
  },
  fromDto: (configuration) => configuration,
  toDto: (configuration) => configuration,
  EditItem: (props) => {
    const { t } = useTranslation();

    return (
      <>
        <TextInput
          label={t(props.propertyKeys.host)}
          value={props.integration.configuration.host}
          onBlur={(e) => {
            props.updateConfiguration("host", e.target.value);
          }}
          errorMessage={props.getError("host")}
          required
          info={{
            title: t(props.propertyKeys.host),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.host }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_HOST_DESCRIPTION),
          }}
        />

        <NumberInput
          label={t(props.propertyKeys.port)}
          value={props.integration.configuration.port}
          minValue={PORT_MIN_VALUE}
          maxValue={PORT_MAX_VALUE}
          onBlur={(value) => props.updateConfiguration("port", value)}
          errorMessage={props.getError("port")}
          required
          info={{
            title: t(props.propertyKeys.port),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.port }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_PORT_DESCRIPTION),
          }}
        />

        <Checkbox
          block
          label={t(props.propertyKeys.useSSL)}
          checked={props.integration.configuration.useSSL}
          setChecked={(value) => {
            props.updateConfiguration("useSSL", value);
          }}
          info={{
            title: t(props.propertyKeys.useSSL),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.useSSL }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_SSL_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.username)}
          value={props.integration.configuration.username}
          onBlur={(e) => {
            props.updateConfiguration("username", e.target.value);
          }}
          errorMessage={props.getError("username")}
          required
          info={{
            title: t(props.propertyKeys.username),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.username }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_USERNAME_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.password)}
          value={props.integration.configuration.password}
          onBlur={(e) => {
            props.updateConfiguration("password", e.target.value);
          }}
          errorMessage={props.getError("password")}
          required
          info={{
            title: t(props.propertyKeys.password),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.password }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_PASSWORD_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.database)}
          value={props.integration.configuration.database}
          onBlur={(e) => {
            props.updateConfiguration("database", e.target.value);
          }}
          errorMessage={props.getError("database")}
          required
          info={{
            title: t(props.propertyKeys.database),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.database }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_DATABASE_DESCRIPTION),
          }}
        />

        <TextInput
          label={t(props.propertyKeys.collection)}
          value={props.integration.configuration.collection}
          onBlur={(e) => {
            props.updateConfiguration("collection", e.target.value);
          }}
          errorMessage={props.getError("collection")}
          required
          info={{
            title: t(props.propertyKeys.collection),
            alt: t(($) => $.INTEGRATIONS_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.collection }),
            description: t(($) => $.MONGODB_INTEGRATION_CONFIGURATION_COLLECTION_DESCRIPTION),
          }}
        />
      </>
    );
  },
};

export default initIntegrationDefinition(MongoDBIntegration);
